/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { ActivatedRoute, Router } from '@angular/router';
import { BaseComponent } from '../../../../../../core/components';
import { ClusterApiService } from '../../../../shared/http';
import { ClusterService } from '../../../../shared/services/utilities';
import { Component, OnInit } from '@angular/core';
import { ErrorResponseModel } from '../../../../../../core/models';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { LoadingService, LogService, ModalService, NotifyService, UserService } from '../../../../../../core/services';
import { NodeModel } from '../../../../shared/models';
import { Observable, Subscriber } from 'rxjs';
import { ipValidator, portRangeValidation } from '../../../../../../core/directives';
import { concatMap, takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-lb-add-node',
    templateUrl: './addRestoreNode.component.html',
})

export class AddRestoreNodeComponent extends BaseComponent implements OnInit {
    /**
     * To store the cluster form group.
     */
    public formGroup: FormGroup;

    /**
     * Logged in appliance Ip Address.
     */
    public currentIP: string;

    /**
     * Array for the NodeModel, holds results from the API inc. services and their restart/reload
     * capabilities
     */
    public nodes: NodeModel[];

    /**
     * Store the id of the node to restore.
     */
    public nodeId: string;

    /**
     * Store the page title.
     */
    public pageTitle: string;

    constructor(
        private clusterApiService: ClusterApiService,
        private clusterService: ClusterService,
        private formBuilder: FormBuilder,
        private loadingService: LoadingService,
        private logService: LogService,
        private modalService: ModalService,
        private notifyService: NotifyService,
        private route: ActivatedRoute,
        private router: Router,
        private userService: UserService
    ) {
        super();
        this.setupForm();

        this.nodeId = this.route.snapshot.paramMap.get('id');
        this.pageTitle = this.nodeId ? 'ha_conf_restore_node' : 'ha_conf_add_node';
    }

    public ngOnInit(): void {
        this.currentIP = this.userService.getUser('applianceAddress');
    }

    /**
     * Confirm pair/restore.
     * @param restore
     */
    public confirmConfiguration(restore?: any): void {
        const confirmationObject = {
            title: restore ? 'ha_conf_restore_confirmation' : 'ha_conf_join_confirmation',
            message: restore ? 'ha_conf_restore_confirmation_message' : 'ha_conf_join_confirmation_message',
            closeInternally: false,
            callBack: this.submit.bind(this, restore),
            options: { translateText: true }
        };

        this.modalService.confirmDecision(confirmationObject);
    }

    /**
     * Submit either restore or add.
     * @param restore
     * @private
     */
    private submit(restore: boolean): Observable<any> {
        return new Observable((observer: Subscriber<any>): void => {
            const apiCall: any = this.setUpBeforeApiCall(restore);
            apiCall
                .pipe(
                    concatMap(() => this.clusterApiService.getStatus()), // After the changes get the latest node config.
                    takeUntil(this.destroyed$))
                .subscribe({
                    next: (nodeState: any): void => {
                        this.clusterService.addNode(nodeState); // Update the cluster service.
                        const route: string[] = restore ? ['../..'] : ['..'];
                        this.router.navigate(route, { relativeTo: this.route }).then();
                        observer.next();
                        observer.complete();
                    },
                    error: (error: ErrorResponseModel): void => {
                        this.handleErrors(restore, error);
                        observer.error(error);
                        observer.complete();
                    }
                })
                .add((): void => {
                    this.loadingService.loadingButtonEmitter.next(false);
                    this.modalService.close('confirmation-box');
                });
        });
    }

    /**
     * Set up before API call.
     * @param restore
     * @private
     */
    private setUpBeforeApiCall(restore: boolean): any {
        this.notifyService.muteLocalNotifications();
        if (restore) {
            this.formGroup.get('restoreNode').setValue(this.nodeId);
        }

        const apiCall = restore
            ? this.clusterApiService.restoreNode(this.formGroup.value, { timeout: 20000 })
            : this.clusterApiService.pair(this.formGroup.value);

        return apiCall;
    }

    /**
     * Handle errors and set notifications.
     * @param restore
     * @param error
     * @private
     */
    private handleErrors(restore: boolean, error: ErrorResponseModel): void {
        this.notifyService.unmuteLocalNotifications();
        if (restore) {
            this.notifyService.error('Error restoring node: ' + error.error.errors[0].message, { id: 'cluster-pair-post' });
        } else {
            this.notifyService.error('Error adding node: ' + error.error.errors[0].message, { id: 'cluster-pair-post' });
        }
        this.logService.error('addRestoreNode: pairWithHACluster', 'Error adding node to cluster', error);
    }

    /**
     * Setup cluster form.
     * @private
     */
    private setupForm(): void {
        this.formGroup = this.formBuilder.group({
            ip: [null, [Validators.required, ipValidator]],
            port: [this.guessPeerAPIPort(), [Validators.required, portRangeValidation]],
            email: [null, Validators.required, Validators.email],
            password: [null, Validators.required],
            restoreNode: [{ value: null, disabled: !!this.nodeId }]
        });
    }

    /**
     * Guess what the peer api port might be based on the configured api port for the appliance
     * we are instigating the paring from. Default port number to 443.
     * @private
     */
    private guessPeerAPIPort(): number {
        if (this.userService.getUser().appliancePort == null) {
            return 443;
        } else {
            return parseInt(this.userService.getUser().appliancePort, 10);
        }
    }
}
